import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { auth, sendPasswordResetEmail } from '../firebase';
import { useTranslation } from '../contexts/LanguageContext';
import '../App.css';

/**
 * ForgotPassword — stranica za zatražiti email za reset lozinke.
 * Koristi Firebase Auth sendPasswordResetEmail.
 * Firebase automatski šalje email s linkom kroz kojega korisnik
 * postavi novu lozinku (hosted reset flow iz Firebase-a).
 */
const ForgotPassword = () => {
  const [email, setEmail] = useState('');
  const [status, setStatus] = useState(null); // null | 'sending' | 'sent' | 'error'
  const [errorMsg, setErrorMsg] = useState('');
  const { t } = useTranslation();

  const handleReset = async (e) => {
    e.preventDefault();
    if (!email.trim()) {
      setStatus('error');
      setErrorMsg(t('auth.forgotEmpty'));
      return;
    }

    setStatus('sending');
    setErrorMsg('');

    try {
      await sendPasswordResetEmail(auth, email.trim());
      // Uvijek pokaži success state — ne odajemo postoji li account na tom emailu
      // (sigurnosna praksa: sprječava user enumeration).
      setStatus('sent');
    } catch (err) {
      console.error('Reset password error:', err);
      // Firebase vraća auth/user-not-found, auth/invalid-email, auth/too-many-requests...
      if (err.code === 'auth/invalid-email') {
        setStatus('error');
        setErrorMsg(t('auth.forgotInvalid'));
      } else if (err.code === 'auth/too-many-requests') {
        setStatus('error');
        setErrorMsg(t('auth.forgotTooMany'));
      } else {
        // Za sve ostalo (uključujući user-not-found) — pokaži success da ne odajemo postojanje accounta.
        setStatus('sent');
      }
    }
  };

  return (
    <div className="container">
      <h2>{t('auth.forgotTitle')}</h2>

      {status === 'sent' ? (
        <div>
          <p style={{ marginBottom: 12 }}>
            {t('auth.forgotSentStart')} <strong>{email}</strong>, {t('auth.forgotSentEnd')}
          </p>
          <p style={{ color: 'var(--text-2)', fontSize: 14, marginBottom: 20 }}>
            {t('auth.forgotSpam')}
          </p>
          <Link to="/login" className="btn btn-secondary">
            {t('auth.backToLogin')}
          </Link>
        </div>
      ) : (
        <>
          <p style={{ color: 'var(--text-2)', marginBottom: 16 }}>
            {t('auth.forgotIntro')}
          </p>

          {status === 'error' && errorMsg && (
            <p className="error-message">{errorMsg}</p>
          )}

          <form onSubmit={handleReset} method="post" action="#" aria-label={t('auth.resetAria')}>
            <label htmlFor="forgot-email" className="sr-only">{t('auth.emailLabel')}</label>
            <input
              id="forgot-email"
              name="email"
              type="email"
              autoComplete="username"
              inputMode="email"
              spellCheck="false"
              autoCapitalize="none"
              autoCorrect="off"
              required
              placeholder="tvoj@email.com"
              className="input-field"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              disabled={status === 'sending'}
              autoFocus
            />
            <button type="submit" disabled={status === 'sending'}>
              {status === 'sending' ? t('auth.sending') : t('auth.sendReset')}
            </button>
          </form>

          <p style={{ marginTop: 16 }}>
            {t('auth.remembered')} <Link to="/login">{t('auth.backToLogin')}</Link>
          </p>
        </>
      )}
    </div>
  );
};

export default ForgotPassword;
